import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t, type I18nKey } from '@/i18n';
import { cardDescription, cardName, type CardDef } from '@/upgrades/cards';
import { RARITY_COLOR, cardTint, drawCardIcon, iconTint } from './card-icons';
import { GLYPH_INK } from './card-glyphs-combat';
import { cardHeadlines, type Headline } from './card-headline';
import { deltaGlyph } from './hangar-icons';
import { bodyText, chamferPanel, hexPath, label } from './widgets';

export const CARD_W = 196;
/** Total card slot height (face + the commerce strip below it) */
export const CARD_H = 296;
/** Face panel height — price/lock live BELOW the face (docs/12 M6) */
export const FACE_H = 252;

const BAND = { x: 12, y: 6, h: 26 } as const;
const ICON_Y = 74;
const SOCKET_R = 32;
/** Wave 4: the BIG signed number sits under the glyph; text moves down */
const HEADLINE_Y = { one: 130, two: [120, 148] } as const;
const HEADLINE_SIZE = { one: 30, two: 22 } as const;
const NAME_Y = 120;
const NAME_Y_HEADLINE = 166;
const EFFECT_TOP = 148;
const EFFECT_TOP_HEADLINE = 190;
const EFFECT_PAD = 8;
const EFFECT_SIZE = 13;
/** Smallest effect text when a long translation has to shrink (docs/03 W4) */
const EFFECT_MIN_SIZE = 12;
const LEVEL_PILL_H = 18;

/**
 * The ONE card face — identical in the shop, the game-over burn picker and
 * anywhere else a card is shown (docs/12 K3). W4 (docs/03): only the parts
 * that carry information stay —
 * - rarity band on top (word + stack level pill), frame in rarity colour;
 * - the stat glyph on a single hex socket, tinted by STAT family;
 * - wave 4: the BIG signed number with a ▲/▼ in the stat-family colour
 *   (trade-offs: two lines, the cost in red) — readable without words;
 * - the name and the effect text, small and secondary.
 * Commerce bits (price, lock) are layered on top by the shop.
 */
export function buildCardFace(
  card: CardDef,
  level: number,
  opts: { stroke?: number; strokeWidth?: number; fill?: number } = {},
): Container {
  const c = new Container();
  const rarity = RARITY_COLOR[card.rarity];
  const tint = cardTint(card);

  const panel = new Graphics();
  chamferPanel(panel, CARD_W, FACE_H, {
    fill: opts.fill ?? 0x161222,
    stroke: opts.stroke ?? rarity,
    strokeWidth: opts.strokeWidth ?? 2,
    notch: 14,
  });
  // Rarity band: rarity reads first, from across the room
  panel.poly([
    BAND.x + 4, BAND.y, CARD_W - 8, BAND.y, CARD_W - 8, BAND.y + BAND.h, BAND.x, BAND.y + BAND.h,
  ]).fill({ color: rarity, alpha: 0.92 });
  c.addChild(panel);

  const rarityTag = label(t(`cardrarity.${card.rarity}` as I18nKey), 13, GLYPH_INK, 2);
  rarityTag.anchor.set(0, 0.5);
  rarityTag.position.set(BAND.x + 12, BAND.y + BAND.h / 2);
  c.addChild(rarityTag);
  const pillW = levelPill(c, CARD_W - 12, BAND.y + BAND.h / 2, level);
  const roomForTag = CARD_W - 12 - pillW - BAND.x - 20;
  if (rarityTag.width > roomForTag) rarityTag.scale.set(roomForTag / rarityTag.width);

  // One hex socket, one glyph — the stat-family tint does the talking
  const socket = new Graphics();
  hexPath(socket, CARD_W / 2, ICON_Y, SOCKET_R).fill(GLYPH_INK).stroke({ color: tint, width: 2.5 });
  c.addChild(socket);
  const icon = drawCardIcon(new Graphics(), card.icon, tint);
  icon.scale.set(0.95);
  icon.position.set(CARD_W / 2, ICON_Y);
  c.addChild(icon);

  const lines = cardHeadlines(card, level);
  drawHeadlines(c, lines, {
    cx: CARD_W / 2, one: HEADLINE_Y.one, two: HEADLINE_Y.two,
    sizeOne: HEADLINE_SIZE.one, sizeTwo: HEADLINE_SIZE.two,
  });
  const name = label(cardName(card), lines.length > 0 ? 14 : 16);
  name.anchor.set(0.5, 0);
  name.position.set(CARD_W / 2, lines.length > 0 ? NAME_Y_HEADLINE : NAME_Y);
  if (name.width > CARD_W - 20) name.scale.set((CARD_W - 20) / name.width);
  c.addChild(name);

  effectLines(c, card, lines.length > 0 ? EFFECT_TOP_HEADLINE : EFFECT_TOP, lines.length > 0);
  return c;
}

/** Where a face puts its headline row(s) — the shop card and the phone card. */
export interface HeadlineLayout {
  cx: number;
  one: number;
  two: ReadonlyArray<number>;
  sizeOne: number;
  sizeTwo: number;
}

/** Big signed number(s): ▲/▼ + value in the stat-family colour (red = cost). */
export function drawHeadlines(c: Container, lines: ReadonlyArray<Headline>, L: HeadlineLayout): void {
  const two = lines.length > 1;
  const size = two ? L.sizeTwo : L.sizeOne;
  lines.forEach((ln, i) => {
    const y = two ? L.two[i] ?? L.two[1]! : L.one;
    const color = ln.good ? iconTint(ln.icon) : COLORS.statDown;
    const row = new Container();
    const g = new Graphics();
    let x = 0;
    if (two) {
      drawCardIcon(g, ln.icon, color).scale.set(0.42);
      g.position.set(10, 0);
      x = 26;
    }
    const arrow = deltaGlyph(new Graphics(), x + 8, 0, size * 0.3, color, ln.up);
    const num = label(ln.text, size, color, 1);
    num.anchor.set(0, 0.5);
    num.position.set(x + 20, 1);
    row.addChild(g, arrow, num);
    const w = num.x + num.width;
    row.position.set(L.cx - w / 2, y);
    c.addChild(row);
  });
}

/** Dark "LV n" pill at the right end of the rarity band. @returns its width */
export function levelPill(c: Container, right: number, cy: number, level: number): number {
  const txt = label(t('shop.level', { n: level }), 13, 0xffffff, 1);
  txt.anchor.set(1, 0.5);
  const w = txt.width + 14;
  const g = new Graphics();
  g.roundRect(right - w, cy - LEVEL_PILL_H / 2, w, LEVEL_PILL_H, LEVEL_PILL_H / 2).fill(GLYPH_INK);
  c.addChild(g);
  txt.position.set(right - 7, cy);
  c.addChild(txt);
  return w;
}

/**
 * Effect text in one quiet inset (no per-row frames, junctions or bus
 * rails). With a headline the numbers are already up top, so the text is
 * the small, secondary description. Without one: plain cards centre the
 * description; trade-offs show a green gain + red cost line with glyphs.
 */
function effectLines(c: Container, card: CardDef, top: number, secondary: boolean): void {
  const x = 12;
  const w = CARD_W - 24;
  const h = FACE_H - top - 12;
  const inset = new Graphics();
  inset.roundRect(x, top, w, h, 8).fill({ color: GLYPH_INK, alpha: 0.85 });
  c.addChild(inset);

  if (!card.tradeoff || secondary) {
    const color = secondary ? 0xaab0ba : 0xe2e6ee;
    const body = fitText(cardDescription(card), color, w - EFFECT_PAD * 2, h - EFFECT_PAD * 2, true);
    body.anchor.set(0.5, 0.5);
    body.position.set(CARD_W / 2, top + h / 2);
    c.addChild(body);
    return;
  }
  const rows = [
    { icon: card.tradeoff.up, text: t(`card.${card.id}.up` as I18nKey), color: COLORS.statUp },
    { icon: card.tradeoff.down, text: t(`card.${card.id}.down` as I18nKey), color: COLORS.statDown },
  ];
  const textW = w - EFFECT_PAD * 2 - 26;
  const rowH = (h - EFFECT_PAD * 2) / rows.length;
  rows.forEach((row, i) => {
    const cy = top + EFFECT_PAD + rowH * (i + 0.5);
    const glyph = drawCardIcon(new Graphics(), row.icon, row.color);
    glyph.scale.set(0.5);
    glyph.position.set(x + EFFECT_PAD + 9, cy);
    c.addChild(glyph);
    const body = fitText(row.text, row.color, textW, rowH, false);
    body.anchor.set(0, 0.5);
    body.position.set(x + EFFECT_PAD + 24, cy);
    c.addChild(body);
  });
}

/** Wrapped body text that steps down to `min` px before overflowing. */
export function fitText(
  text: string, color: number, w: number, maxH: number, center: boolean,
  start = EFFECT_SIZE, min = EFFECT_MIN_SIZE,
) {
  let size = start;
  let body = bodyText(text, size, color, w);
  while (body.height > maxH && size > min) {
    body.destroy();
    size -= 1;
    body = bodyText(text, size, color, w);
  }
  body.style.align = center ? 'center' : 'left';
  return body;
}
